import { useMemo } from 'react';
import { expand } from 'uri-template-lite';
import { apiClient } from '../../common/ApiClient';
import { getFromMapSafely, getMapBy } from '../../common/utils';
import { DataObjectGroupUrl } from './DataObjectGroupUrl';
import { createApiQueryHook } from '../../common/queryClient/createApiQueryHook';
import {
  DataObjectGroupWithDescriptionsDto,
  type DataObjectMiniDto,
} from '../../dataobject/types';

export interface DataObjectGroupWithDescriptionsQueryParams {
  groupType?: string;
  outputType?: string;
  active?: boolean;
}

export const getDataObjectGroupsWithDescriptionsQuery = (
  params?: DataObjectGroupWithDescriptionsQueryParams,
) =>
  apiClient
    .get<
      DataObjectGroupWithDescriptionsDto[]
    >(expand(DataObjectGroupUrl.WITH_DESCRIPTIONS, { active: true, ...params }))
    .then((response) => response.data);

export const dataObjectGroupsWithDescriptionsQueryKey = [
  'data-object-groups-with-descriptions',
] as const;

const useDataObjectGroupsWithDescriptionsListQuery = createApiQueryHook(
  dataObjectGroupsWithDescriptionsQueryKey,
  getDataObjectGroupsWithDescriptionsQuery,
);

export type DataObjectGroupsWithDescriptionsMapById = Map<
  number | null,
  DataObjectGroupWithDescriptionsDto
>;

/**
 * Turns the list of groups with descriptions into a map by group id, where descriptions that
 * do not belong to any (business) group are mapped into synthetic systemic groups.
 *
 *  type DataObjectGroupWithDescriptionsDto = {
 *    id: number | null,    // group ID or null for ungrouped descriptions
 *    isSystemic: boolean,  // server returns only business groups with this set to false, but for ungrouped descriptions we set this to true to distinguish them from regular groups
 *    code: string,
 *    name: string,
 *    groupType: string,
 *    dataObjectGroupData: object,
 *    descriptions: DataObjectMiniDto[]
 *  }
 *
 * Response from server: Array<DataObjectGroupWithDescriptionsDto>
 * This hook returns: Map<number | null, DataObjectGroupWithDescriptionsDto>
 */
export const useDataObjectGroupsWithDescriptionsQuery = (
  ...args: Parameters<typeof useDataObjectGroupsWithDescriptionsListQuery>
) => {
  const { data: groupsList, ...rest } =
    useDataObjectGroupsWithDescriptionsListQuery(...args);

  const groupsById: DataObjectGroupsWithDescriptionsMapById | undefined =
    useMemo(() => {
      if (!groupsList) {
        return undefined;
      }
      const businessGroupsById = getMapBy(groupsList, 'id');
      const ungroupedDescriptions = getFromMapSafely(
        businessGroupsById,
        null,
      ).descriptions;

      const descriptionsMap = getMapBy(
        ungroupedDescriptions,
        'dataObjectGroupId',
        mapUngroupedDescriptionIntoSystemicGroup,
      );
      return new Map([...businessGroupsById, ...descriptionsMap]);
    }, [groupsList]);

  return { data: groupsById, ...rest };
};

const mapUngroupedDescriptionIntoSystemicGroup = (
  description: DataObjectMiniDto,
) => ({
  id: description.dataObjectGroupId,
  isSystemic: true,
  code: description.code,
  name: description.name,
  groupType: description.groupType,
  dataObjectGroupData: description.dataObjectGroupData,
  descriptions: [description],
  frequency: description.dataObjectGroupData?.frequency || null,
});
